import { createMemoryHistory, createRouter, type RouteRecordRaw } from "vue-router"

/**
 * Routes in the ACP that can be accessed without being authenticated. They are used for staff actions that are performed
 * on dedicated devices, e.g., tablets that scan QR codes.
 *
 * The definitions live here (and not in ./index.ts) so that the public-route detection below can be imported without
 * pulling in the main ACP router. The main router binds to the browser history via createWebHistory, which mutates
 * window.history as a side effect of being imported — that must never happen in the shop bundle, which also loads
 * vf.config.ts and therefore isPublicAcpRoute(). Importing this leaf module statically (rather than dynamically) also
 * keeps the ACP router inside the main static graph, avoiding a dynamic-import chunk split that previously caused a
 * circular-init "X is not a function" crash in the production ACP build.
 */
export const publicAcpRoutes: RouteRecordRaw[] = [
    {
        path: "/device/packaging/:token",
        name: "packaging",
        component: () => import("../pages/PackagingPage.vue"),
        props: true,
        meta: {
            public: true,
            persistent: true,
            bodyClass: "packaging",
            hideLayout: true,
            title: "Paketinhalt dokumentieren",
        },
    },
    {
        path: "/device/pickup-confirmation/:token",
        name: "pickup-confirmation",
        component: () => import("@/acp/pages/PickupConfirmationPage.vue"),
        props: true,
        meta: {
            public: true,
            persistent: true,
            bodyClass: "pickup-confirmation",
            hideLayout: true,
            title: "Empfang bestätigen",
        },
    },
]

// The ACP runs under the "/acp" base path in dev, but at the root in production. location.pathname therefore carries the
// "/acp" prefix in dev, which must be stripped before matching against the route paths above.
const base = import.meta.env.DEV ? "/acp" : ""

/**
 * A minimal, in-memory router used solely to match a pathname against the public routes. It uses createMemoryHistory so
 * that importing this module has no side effect on the browser history — unlike the main ACP router (see ./index.ts).
 */
const matcher = createRouter({
    history: createMemoryHistory(),
    routes: [
        ...publicAcpRoutes,
        // catch-all so that resolving a non-public path does not emit a "no match" warning
        { path: "/:pathMatch(.*)*", name: "_non_public", component: { render: () => null }, meta: { public: false } },
    ],
})

/**
 * Determines whether the given pathname (defaults to the current location) maps to a public ACP route, i.e., one that may
 * be accessed without authentication.
 */
export function isPublicAcpRoute(pathname = location.pathname): boolean {
    const path = base && pathname.startsWith(base) ? pathname.slice(base.length) || "/" : pathname

    return matcher.resolve(path).meta.public === true
}
